// src/services/auth.js

import axios from 'axios';

// Determine backend at runtime without .env
const isLocal =
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1';
const API_URL = isLocal
  ? 'http://localhost:8000'    // local Django dev server
  : 'https://bonds-backend-454480edb0c7.herokuapp.com';  // production backend

// Configure axios base URL globally
axios.defaults.baseURL = API_URL;

// Restore Authorization header from localStorage (if any)
const stored = localStorage.getItem('user');
if (stored) {
  try {
    const { access } = JSON.parse(stored);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access}`;
  } catch {
    // ignore malformed storage
  }
}

/**
 * Log in with username or email + password
 * Chooses payload key based on identifier format
 * POST /api/login/
 */
export const login = async (identifier, password) => {
  const isEmail = identifier.includes('@');
  const payload = isEmail
    ? { email: identifier, password }
    : { username: identifier, password };

  const response = await axios.post('/api/login/', payload);

  if (response.data.access) {
    localStorage.setItem('user', JSON.stringify(response.data));
    axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access}`;
  }

  return response.data;
};

/** Clear tokens & header */
export const logout = () => {
  localStorage.removeItem('user');
  delete axios.defaults.headers.common['Authorization'];
};

/** Retrieve stored auth payload */
export const getCurrentUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user'));
  } catch {
    return null;
  }
};

/**
 * Refresh access token using stored refresh token
 * POST /api/token/refresh/
 */
export const refreshToken = async () => {
  const stored = getCurrentUser();
  if (!stored?.refresh) throw new Error('No refresh token available');

  const { data } = await axios.post('/api/token/refresh/', {
    refresh: stored.refresh,
  });

  const updated = { ...stored, access: data.access };
  localStorage.setItem('user', JSON.stringify(updated));
  axios.defaults.headers.common['Authorization'] = `Bearer ${data.access}`;
  return data.access;
};

// 401 interceptor: on 401 (except when calling refresh), try refresh → retry once
axios.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.endsWith('/api/token/refresh/')
    ) {
      originalRequest._retry = true;
      try {
        await refreshToken();
        return axios(originalRequest);
      } catch {
        logout();
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

const AuthService = {
  login,
  logout,
  getCurrentUser,
  refreshToken,
};

export default AuthService;
